import React from "react";
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Maximize2, X } from "lucide-react";

/**
 * Heatmap de ESTADOS de alineación (no de similitud numérica).
 *
 * Réplica de graficar_heatmap_alineacion() del notebook del autor: cada celda
 * es el estado categórico del par Master × IES, no un valor continuo.
 *   0 = No similitud (celeste)  1 = Diferencia (azul)  2 = Similitud (azul oscuro)
 *
 * Recibe visualization_data.alignment_heatmap del backend:
 *   { z, x (IES), y (Master), text, value_map, colors }
 *
 * Con glosarios grandes la matriz no cabe en la tarjeta y aparece scroll; el
 * botón de ampliar la abre en un diálogo a pantalla casi completa, con celdas
 * y etiquetas más grandes.
 */

const HeatmapContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "none",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

// Paleta del autor (celeste -> azul). Se usa como respaldo si el backend
// no envía `colors` en el payload.
const FALLBACK_COLORS = {
  "No similitud": "#87CEFA",
  Diferencia: "#1E90FF",
  Similitud: "#00008B",
};

const ESTADOS_LEYENDA = ["Similitud", "Diferencia", "No similitud"];

// Medidas de la vista ampliada. Se declaran aquí para que el cálculo del
// tamaño de celda y el render usen exactamente los mismos valores.
const LABEL_WIDTH_AMPLIADO = 230;
const ALTO_ETIQUETAS_X_AMPLIADO = 150;
const ALTO_CABECERA_AMPLIADO = 150;

/** Leyenda de estados, compartida por la vista normal y la ampliada. */
const Leyenda = ({ colors, grande = false }) => (
  <Box
    sx={{
      display: "flex",
      gap: grande ? 2.5 : 1.5,
      justifyContent: "flex-end",
      flexWrap: "wrap",
      mb: 0.5,
      flexShrink: 0,
    }}
  >
    {ESTADOS_LEYENDA.map((estado) => (
      <Box key={estado} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            width: grande ? 14 : 10,
            height: grande ? 14 : 10,
            borderRadius: "2px",
            backgroundColor: colors[estado],
            border: "1px solid rgba(0,0,0,0.15)",
          }}
        />
        <Typography
          sx={{
            fontSize: grande ? "13px" : "10px",
            color: "text.secondary",
          }}
        >
          {estado}
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * La matriz en sí. El ancho y el alto de celda son independientes (`cellW` /
 * `cellH`) para que cada dimensión aproveche su propio espacio: con muchas
 * filas y pocas columnas, las celdas se ensanchan en vez de encogerse las dos.
 */
const Matriz = ({
  masterLabels,
  iesLabels,
  estadoDeCelda,
  colors,
  cellW,
  cellH,
  labelWidth,
  fontSize,
  altoEtiquetasX,
  estirar = true,
}) => (
  <Box sx={{ display: "inline-block", minWidth: estirar ? "100%" : undefined }}>
    {masterLabels.map((masterName, i) => (
      <Box key={`row-${i}`} sx={{ display: "flex", alignItems: "center" }}>
        <Tooltip title={masterName} placement="left">
          <Typography
            sx={{
              width: `${labelWidth}px`,
              minWidth: `${labelWidth}px`,
              fontSize: `${fontSize}px`,
              textAlign: "right",
              pr: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: "text.secondary",
            }}
          >
            {masterName}
          </Typography>
        </Tooltip>
        {iesLabels.map((iesName, j) => {
          const estado = estadoDeCelda(i, j);
          return (
            <Tooltip
              key={`c-${i}-${j}`}
              title={`${masterName} × ${iesName}: ${estado}`}
              arrow
            >
              <Box
                sx={{
                  width: `${cellW}px`,
                  height: `${cellH}px`,
                  minWidth: `${cellW}px`,
                  backgroundColor:
                    colors[estado] || FALLBACK_COLORS["No similitud"],
                  border: "1px solid #f0f0f0",
                  cursor: "pointer",
                  transition: "transform 0.15s ease",
                  "&:hover": {
                    transform: "scale(1.15)",
                    zIndex: 2,
                    border: "1px solid #333",
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    ))}

    {/* Etiquetas IES rotadas al pie */}
    <Box sx={{ display: "flex", mt: 0.5 }}>
      <Box sx={{ width: `${labelWidth}px`, minWidth: `${labelWidth}px` }} />
      {iesLabels.map((iesName, j) => (
        <Box
          key={`x-${j}`}
          sx={{
            width: `${cellW}px`,
            minWidth: `${cellW}px`,
            height: altoEtiquetasX,
            position: "relative",
          }}
        >
          <Tooltip title={iesName} placement="bottom">
            <Typography
              sx={{
                position: "absolute",
                top: 4,
                left: "50%",
                transformOrigin: "left top",
                transform: "rotate(60deg)",
                fontSize: `${fontSize}px`,
                whiteSpace: "nowrap",
                color: "text.secondary",
                maxWidth: altoEtiquetasX - 4,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {iesName}
            </Typography>
          </Tooltip>
        </Box>
      ))}
    </Box>
  </Box>
);

const MaterialAlignmentHeatmap = ({ data }) => {
  const [ampliado, setAmpliado] = React.useState(false);

  // Tamaño de la ventana: la vista ampliada calcula con él el tamaño de celda
  // para que la matriz entre completa. Se declara antes de cualquier return
  // condicional para no romper el orden de los hooks.
  const [ventana, setVentana] = React.useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  }));

  React.useEffect(() => {
    const alRedimensionar = () =>
      setVentana({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", alRedimensionar);
    return () => window.removeEventListener("resize", alRedimensionar);
  }, []);

  const iesLabels = React.useMemo(() => (data && data.x) || [], [data]);
  const masterLabels = React.useMemo(() => (data && data.y) || [], [data]);
  const maxCols = Math.max(iesLabels.length, 1);
  const maxRows = Math.max(masterLabels.length, 1);

  // En el diálogo la matriz debe verse COMPLETA, sin barras de desplazamiento:
  // la celda se ajusta al menor tamaño que permitan el ancho y el alto reales
  // de la ventana, descontando etiquetas, cabecera y paddings del diálogo.
  // La celda de la vista ampliada NO es cuadrada: el ancho se reparte el
  // espacio horizontal y el alto el vertical, de forma independiente. Si se
  // usara el mínimo de ambos (celda cuadrada), con muchas filas la matriz
  // quedaría diminuta y dejaría medio diálogo vacío a los lados.
  const celdaAmpliada = React.useMemo(() => {
    const anchoDisponible =
      ventana.w * 0.94 - LABEL_WIDTH_AMPLIADO - 80;
    const altoDisponible =
      ventana.h * 0.92 - ALTO_CABECERA_AMPLIADO - ALTO_ETIQUETAS_X_AMPLIADO;
    let w = Math.max(8, Math.min(60, Math.floor(anchoDisponible / maxCols)));
    const h = Math.max(8, Math.min(60, Math.floor(altoDisponible / maxRows)));
    // Se limita cuánto puede alargarse la celda: sin tope, con muchas filas y
    // pocas columnas quedarían franjas muy estiradas, difíciles de leer.
    w = Math.min(w, Math.max(h * 2.2, 16));
    return { w, h };
  }, [ventana, maxCols, maxRows]);

  if (!data || !data.z || !data.z.length) {
    return (
      <HeatmapContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Sin datos de alineación
          </Typography>
        </Box>
      </HeatmapContainer>
    );
  }

  const colors = { ...FALLBACK_COLORS, ...(data.colors || {}) };

  // Invierte value_map (estado -> numero) para pintar desde z.
  const valueMap = data.value_map || {
    "No similitud": 0,
    Diferencia: 1,
    Similitud: 2,
  };
  const numeroAEstado = Object.entries(valueMap).reduce((acc, [k, v]) => {
    acc[v] = k;
    return acc;
  }, {});

  const estadoDeCelda = (i, j) => {
    // `text` ya trae el estado en texto; si falta, se deriva de z.
    if (data.text && data.text[i] && data.text[i][j]) return data.text[i][j];
    return numeroAEstado[data.z[i][j]] || "No similitud";
  };

  // Celdas cuadradas que se encogen cuando hay muchos términos, con un piso
  // para que sigan siendo visibles (el notebook ajusta figsize de forma similar).
  const cellSize = Math.max(14, Math.min(30, Math.floor(560 / maxCols)));

  const propsMatriz = {
    masterLabels,
    iesLabels,
    estadoDeCelda,
    colors,
  };

  return (
    <>
      <HeatmapContainer>
        {/* Leyenda + botón de ampliar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Leyenda colors={colors} />
          <Tooltip title="Ver la matriz completa">
            <IconButton
              size="small"
              onClick={() => setAmpliado(true)}
              sx={{ color: "text.secondary", flexShrink: 0 }}
              aria-label="Ver la matriz completa"
            >
              <Maximize2 size={16} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Matriz con scroll propio: nunca desborda el contenedor padre */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Matriz
            {...propsMatriz}
            cellW={cellSize}
            cellH={cellSize}
            labelWidth={128}
            fontSize={9}
            altoEtiquetasX={90}
          />
        </Box>

        <Typography
          sx={{
            fontSize: "9px",
            color: "text.secondary",
            textAlign: "center",
            flexShrink: 0,
            pt: 0.5,
          }}
        >
          Filas: MasterDomain · Columnas: IESDomain
        </Typography>
      </HeatmapContainer>

      {/* Vista ampliada */}
      <Dialog
        open={ampliado}
        onClose={() => setAmpliado(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "92vh", borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
              Resultados de Alineación
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
              {masterLabels.length} × {iesLabels.length} · Filas:
              MasterDomain · Columnas: IESDomain
            </Typography>
          </Box>
          <IconButton
            onClick={() => setAmpliado(false)}
            size="small"
            aria-label="Cerrar"
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <Leyenda colors={colors} grande />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              pt: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Matriz
              {...propsMatriz}
              cellW={celdaAmpliada.w}
              cellH={celdaAmpliada.h}
              estirar={false}
              labelWidth={LABEL_WIDTH_AMPLIADO}
              // La fuente nunca supera el alto de la celda: si no, el texto de
              // una fila se solaparía con el de la siguiente.
              fontSize={Math.max(7, Math.min(13, celdaAmpliada.h - 2))}
              altoEtiquetasX={ALTO_ETIQUETAS_X_AMPLIADO}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MaterialAlignmentHeatmap;
